import { useEffect, useRef, type ReactNode } from 'react';
import { useAlertView, useConfirmationView } from '~/app';
import { Button } from '~/components/buttons';
import { MessagesSection } from '~/features/market/job-opportunities/sub-components/MessagesSection';
import { ActivationFeeDialog } from '~/features/market/negotiations/sub-components/ActivationFeeDialog';
import {
  MarketContentPanel,
  MarketEmptyPanel,
  MarketLoadingPanel,
  MarketPanelHeader,
  MarketStatusPill,
  MarketWorkspaceGrid,
} from '~/features/shared/market-content';
import { MessageComposer } from '~/features/shared/market-content/MessageComposer';
import { ResponseUnmaskPanel } from '~/features/shared/market-content/ResponseUnmaskPanel';
import {
  declineResponseConfirmation,
  RESPONSE_DECLINED_ALERT_TEXT,
} from '~/features/shared/responseCloseConfirmations';
import { sendSuccessAlert } from '~/features/utils';
import type { ServiceOpportunity } from './schemas';
import { ServiceNegotiationModal } from './ServiceNegotiationModal';
import { offerLabel } from './ServiceNegotiationSummary';
import { ServiceResponseBoundary, useServiceResponseActors } from './ServiceResponseBoundary';
import { ServiceResponseDetail } from './ServiceResponseDetail';
import type { ServiceResponseActors } from './serviceResponseSession.machine';
import { useServiceAcceptFee } from './useServiceAcceptFee';
import { useServiceConversation } from './useServiceConversation';

type Props = {
  responseId: number;
  viewerScopeKey: string;
  currentUserInfoId: number | undefined;
  sidebar: ReactNode;
  onChanged?: () => void;
  refreshKey?: number;
  progressionBusy?: boolean;
  onBeginNegotiation?: (opportunity: ServiceOpportunity) => void;
  onViewProvider?: (providerId: number, responseId: number) => void;
};

export const ServiceConversation = (props: Props) => (
  <ServiceResponseBoundary viewerScopeKey={props.viewerScopeKey}>
    <ConnectedConversation key={`${props.viewerScopeKey}:${props.responseId}`} {...props} />
  </ServiceResponseBoundary>
);

const ConnectedConversation = (props: Props) => {
  const actors = useServiceResponseActors(props.responseId);
  return actors ? (
    <ScopedConversation {...props} actor={actors.conversation} />
  ) : (
    <MarketWorkspaceGrid
      sidebar={props.sidebar}
      detail={<MarketLoadingPanel label="Loading response" />}
      conversation={<MarketLoadingPanel label="Loading messages" />}
    />
  );
};

const ScopedConversation = ({
  responseId,
  viewerScopeKey,
  currentUserInfoId,
  sidebar,
  onChanged,
  refreshKey = 0,
  progressionBusy = false,
  onBeginNegotiation,
  onViewProvider,
  actor,
}: Props & { actor: ServiceResponseActors['conversation'] }) => {
  const state = useServiceConversation(actor, onChanged, refreshKey);
  const confirmationView = useConfirmationView();
  const alertView = useAlertView();
  const alertViewRef = useRef(alertView);
  alertViewRef.current = alertView;
  // [MCV-1850] One toast per acknowledged decline. The conversation actor outlives this view,
  // so a decline already counted at mount is not ours.
  const seenDeclineRevision = useRef(state.declineRevision);
  useEffect(() => {
    if (state.declineRevision === seenDeclineRevision.current) return;
    seenDeclineRevision.current = state.declineRevision;
    alertViewRef.current.send(
      sendSuccessAlert({
        text: RESPONSE_DECLINED_ALERT_TEXT,
        subText: 'The other party has been notified.',
      })
    );
  }, [state.declineRevision]);
  const opportunity = state.thread?.opportunity;
  // The counter editor is a modal over the page. An unconfirmed outcome closes it
  // so the recovery controls stay reachable; the draft survives for later.
  const counterOpen = Boolean(state.counterDraft && opportunity && !state.uncertain);
  const messages = state.thread?.messages ?? [];
  const providerId = opportunity?.counterparty.providerId;
  // [MCV-1858] The hiring employer sees and confirms the activation fee before Accept posts.
  const acceptFee = useServiceAcceptFee({
    responseId,
    viewerRole: opportunity?.viewerRole,
    currentOfferId: opportunity?.latestNegotiation?.id ?? null,
    onAccept: state.accept,
  });
  const competingActionBlocked =
    progressionBusy ||
    !state.ready ||
    state.uncertain !== null ||
    state.counterDraft !== null ||
    acceptFee.previewing;
  // Several action errors ask for a refresh, so offer one with any error.
  const showRefresh = Boolean(state.readError || state.error || state.counterAvailabilityError);
  const showAcknowledge = state.uncertain !== null && state.ready;
  const refresh = () => {
    state.refresh();
    onChanged?.();
  };
  const notices = (
    <div className="space-y-3">
      <p role="status" aria-live="polite" className="min-h-5 text-sm">
        {[counterOpen ? null : state.error, state.readError].filter(Boolean).join(' ')}
      </p>
      {(showRefresh || showAcknowledge) && (
        <div className="flex flex-wrap gap-3">
          {showRefresh && (
            <Button
              type="button"
              size="sm"
              colorVariant="outline"
              label={state.readError ? 'Try again' : 'Refresh response'}
              disabled={progressionBusy || state.loading || state.writing}
              onClick={refresh}
            />
          )}
          {showAcknowledge && (
            <Button
              type="button"
              size="sm"
              colorVariant="outline"
              label="I checked the response; enable actions"
              onClick={state.acknowledgeUncertainty}
            />
          )}
        </div>
      )}
      {state.uncertain && !state.loading && !state.writing && (
        <div className="space-y-2">
          <p className="text-sm">
            Discarding clears this local recovery and saved draft. It does not cancel or undo server
            work.
          </p>
          <Button
            type="button"
            size="sm"
            colorVariant="outline"
            label="Discard local recovery"
            onClick={state.discardUncertainty}
          />
        </div>
      )}
    </div>
  );
  return (
    <MarketWorkspaceGrid
      dataCy="service-opportunities-grid"
      detailKey={responseId}
      sidebar={sidebar}
      detail={
        opportunity ? (
          <>
            <ServiceResponseDetail
              opportunity={opportunity}
              notices={notices}
              identityControls={
                <ResponseUnmaskPanel
                  responseId={responseId}
                  viewerScopeKey={viewerScopeKey}
                  refreshKey={`${opportunity.updatedAt}:${messages[messages.length - 1]?.id ?? ''}`}
                  disabled={competingActionBlocked}
                  onChanged={refresh}
                />
              }
              actionsDisabled={competingActionBlocked}
              viewProviderDisabled={progressionBusy || !state.ready}
              negotiateDisabled={progressionBusy || !state.ready || state.uncertain !== null}
              onViewProvider={
                providerId != null && onViewProvider
                  ? () => onViewProvider(providerId, responseId)
                  : undefined
              }
              onNegotiate={onBeginNegotiation && (() => onBeginNegotiation(opportunity))}
              onAccept={acceptFee.requestAccept}
              onCounter={() => actor.send({ type: 'OPEN_COUNTER' })}
              onWithdraw={state.withdraw}
              onReject={() => confirmationView.send(declineResponseConfirmation(state.reject))}
            />
            <ActivationFeeDialog {...acceptFee.dialogProps} />
            {state.counterDraft && (
              <ServiceNegotiationModal
                open={counterOpen}
                actor={actor}
                draft={state.counterDraft}
                opportunity={opportunity}
                disabled={progressionBusy || !state.ready || state.uncertain !== null}
                loading={state.counterLoading}
                error={counterOpen ? state.error : null}
                errorRevision={state.errorRevision}
                availabilityError={state.counterAvailabilityError}
                onClose={() => actor.send({ type: 'CANCEL_COUNTER' })}
              />
            )}
          </>
        ) : (
          <MarketContentPanel variant="scroll">
            <div className="space-y-4 p-4 text-left">
              <MarketPanelHeader title="Service response" />
              {notices}
              {state.loading ? (
                <MarketLoadingPanel label="Loading response" />
              ) : (
                <MarketEmptyPanel title="Response unavailable" />
              )}
            </div>
          </MarketContentPanel>
        )
      }
      conversation={
        <MarketContentPanel
          className="h-full"
          variant="scroll"
          bodyClassName="flex min-h-0 flex-1 flex-col p-0"
          header={
            opportunity ? (
              <div className="text-left">
                <h3 className="body-text-sb">{opportunity.counterparty.displayName}</h3>
                <div className="mt-2">
                  <MarketStatusPill
                    status={opportunity.isUnmasked ? 'unmasked' : 'masked'}
                    label={
                      !opportunity.isUnmasked
                        ? 'Identity masked'
                        : opportunity.viewerRole === 'RESPONDER'
                          ? 'Provider'
                          : 'Organization'
                    }
                  />
                </div>
              </div>
            ) : (
              <MarketPanelHeader title="Messages" />
            )
          }
        >
          <MessagesSection
            messages={messages}
            currentUserInfoId={currentUserInfoId}
            loading={state.loading}
            renderMessageContext={(message) => {
              if (message.negotiationId == null || message.negotiationVersion == null) return null;
              const offer = (
                opportunity?.negotiationHistory ?? [opportunity?.latestNegotiation]
              ).find(
                (item) =>
                  item?.id === message.negotiationId && item.version === message.negotiationVersion
              );
              return (
                <p className="mt-2 text-left text-sm">
                  {offer ? (
                    <>
                      {offerLabel(offer)}
                      {offer.id === opportunity?.latestNegotiation?.id
                        ? ' · Current offer'
                        : ' · See negotiation history'}
                    </>
                  ) : (
                    <>
                      Offer version {message.negotiationVersion} — details unavailable. Refresh the
                      response to try again.
                    </>
                  )}
                </p>
              );
            }}
          />
          {opportunity && (
            <MessageComposer
              value={state.draft}
              onChange={state.edit}
              onSend={state.send}
              error={state.sendError}
              disabled={competingActionBlocked}
              sending={state.sending}
              masked={!opportunity.isUnmasked}
            />
          )}
        </MarketContentPanel>
      }
    />
  );
};
